/**
 * Team colour palette — 50 visually distinct colours used to colour calendar
 * leave bars (and legend dots) by TEAM rather than by leave type.
 *
 * Colours are generated deterministically with a golden-angle hue rotation so
 * consecutive entries land far apart on the wheel. A team id is hashed into the
 * palette, so a team keeps the same colour across renders without any stored
 * assignment.
 *
 * For each team the bar gets:
 *   - `bg`   — a solid, vivid fill (opaque).
 *   - `line` — a darker shade of the fill (same hue), for the border.
 *   - `ink`  — white or a dark hue-tint, whichever contrasts better with `bg`
 *              (WCAG relative-luminance pick), so the text is always legible.
 */

/** Number of unique team colours in the palette. */
export const TEAM_COLOR_COUNT = 50

interface Hsl {
  h: number
  s: number
  l: number
}

/**
 * 50 distinct base colours (HSL). Hue advances by the golden angle so any two
 * CONSECUTIVE entries sit ~137° apart on the wheel — that's why teams are
 * assigned by index (see `teamColorForIndex`), giving neighbouring teams
 * maximally-contrasting hues. Lightness is kept in a light-but-coloured band so
 * the hue is clearly readable (not a washed-out pastel) while staying soft.
 */
const TEAM_HSL: readonly Hsl[] = Array.from(
  { length: TEAM_COLOR_COUNT },
  (_unused, i): Hsl => ({
    h: Math.round((i * 137.508) % 360),
    s: 44 + (i % 2) * 10, // 44 / 54 — muted, soft fills
    l: 88 + (i % 3) * 2, // 88 / 90 / 92 — very light pastel (hues stay distinct
    // because consecutive teams are ~137° apart, assigned by index, and the
    // darker-shade border keeps each bar defined)
  }),
)

/** HSL (h 0..360, s/l 0..100) → sRGB channels 0..255. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sN = s / 100
  const lN = l / 100
  const c = (1 - Math.abs(2 * lN - 1)) * sN
  const hp = h / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0
  let g = 0
  let b = 0
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0]
  else if (hp < 2) [r, g, b] = [x, c, 0]
  else if (hp < 3) [r, g, b] = [0, c, x]
  else if (hp < 4) [r, g, b] = [0, x, c]
  else if (hp < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const m = lN - c / 2
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ]
}

/** WCAG relative luminance of an sRGB colour (0..1). */
function relLuminance([r, g, b]: [number, number, number]): number {
  const lin = (v: number): number => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** FNV-1a string hash → non-negative int, stable across runs. */
function hashTeamId(id: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h
}

export interface TeamColorStyle {
  /** Solid, vivid fill. */
  bg: string
  /** Border — a darker shade of the fill. */
  line: string
  /** Text colour chosen for contrast against `bg`. */
  ink: string
  /** Representative swatch for legend dots (same as the fill). */
  swatch: string
}

function styleFromHsl({ h, s, l }: Hsl): TeamColorStyle {
  const bg = `hsl(${h} ${s}% ${l}%)`
  // Border: same hue, clearly darker than the light fill.
  const line = `hsl(${h} ${s}% ${Math.max(l - 34, 36)}%)`
  // Text: white vs. a dark hue-tint — pick whichever contrasts better.
  const bgLum = relLuminance(hslToRgb(h, s, l))
  const whiteContrast = (1 + 0.05) / (bgLum + 0.05)
  const darkTint = `hsl(${h} ${Math.min(s + 6, 92)}% 20%)`
  const darkLum = relLuminance(hslToRgb(h, Math.min(s + 6, 92), 20))
  const darkContrast = (bgLum + 0.05) / (darkLum + 0.05)
  const ink = whiteContrast >= darkContrast ? '#ffffff' : darkTint
  return { bg, line, ink, swatch: bg }
}

/**
 * Colour a team by its INDEX in the team list. Consecutive indices are ~137°
 * apart in hue, so neighbouring teams always get obviously-different colours.
 * Prefer this over `teamColorStyle` whenever the caller knows the team's
 * position among all teams.
 */
export function teamColorForIndex(index: number): TeamColorStyle {
  const i = ((index % TEAM_COLOR_COUNT) + TEAM_COLOR_COUNT) % TEAM_COLOR_COUNT
  return styleFromHsl(TEAM_HSL[i]!)
}

/**
 * Resolve a stable colour for a team id by hashing (no index available). Used
 * as a fallback; distinctness across a small set of teams isn't guaranteed, so
 * prefer `teamColorForIndex` when the full team list is known.
 */
export function teamColorStyle(teamId: string): TeamColorStyle {
  return styleFromHsl(TEAM_HSL[hashTeamId(teamId) % TEAM_HSL.length]!)
}
